import { errorBodyMessage } from '@/utils/nestedErrorMessage';

import { RequestError } from './errors';

/**
 * What a failed request may tell the user.
 *
 * - `client`: the server refused it (4xx). Its message is written for the caller: a
 *   validation or business rule.
 * - `server`: the server failed (5xx). Its text is for the log: a 500 from a dependency can
 *   carry trace ids, file paths or another service's error. Only the request id the backend
 *   logged it under is kept, for support.
 * - `network`: no response came back.
 * - `unknown`: anything else thrown, such as a bug in the browser code.
 */
export type ApiErrorDescription =
    | { kind: 'client'; message?: string }
    | { kind: 'server'; requestId?: string }
    | { kind: 'network' }
    | { kind: 'unknown' };

/** The generic client error the API client rejects with when no response arrived. */
const NO_RESPONSE = 'noInternetConnection';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/** The HTTP status: the client's own, else the `statusCode` Nest puts in the body. */
function statusOf(error: Record<string, unknown>): number | undefined {
    if (typeof error.status === 'number') return error.status;
    const body = error.message;
    return isRecord(body) && typeof body.statusCode === 'number' ? body.statusCode : undefined;
}

export function describeApiError(error: unknown): ApiErrorDescription {
    if (!isRecord(error)) return { kind: 'unknown' };
    const status = statusOf(error);
    if (status === undefined) {
        return RequestError.isRequestError(error) && error.message === NO_RESPONSE
            ? { kind: 'network' }
            : { kind: 'unknown' };
    }
    const body = error.message;
    if (status >= 500) {
        const requestId = isRecord(body) ? body.requestId : undefined;
        return typeof requestId === 'string' && requestId.trim()
            ? { kind: 'server', requestId: requestId.trim() }
            : { kind: 'server' };
    }
    if (status >= 400) {
        const message = errorBodyMessage(body);
        return message ? { kind: 'client', message } : { kind: 'client' };
    }
    return { kind: 'unknown' };
}
